import { useEffect, useState } from "react";
import {
  listEmailTemplates,
  resetEmailTemplate,
  testSendEmailTemplate,
  updateEmailTemplate,
} from "./api";
import { useAdminResource } from "./use-admin-resource";
import { useSubscription } from "./useSubscription";
import { LockedFeature } from "./LockedFeature";
import { useToast } from "../../components/useToast";
import type { EmailTemplate } from "./types";

type EditState = { subject: string; body: string };

function EmailTemplateCard({
  template,
  onSaved,
}: {
  template: EmailTemplate;
  onSaved: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [edit, setEdit] = useState<EditState>({ subject: template.subject, body: template.body });
  const [savePending, setSavePending] = useState(false);
  const [resetPending, setResetPending] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testPending, setTestPending] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  useEffect(() => {
    setEdit({ subject: template.subject, body: template.body });
  }, [template.subject, template.body]);

  async function save() {
    if (!edit.subject.trim() || !edit.body.trim()) return;
    setSavePending(true);
    try {
      await updateEmailTemplate(template.template_key, {
        subject: edit.subject.trim(),
        body: edit.body.trim(),
      });
      showSuccess(`Saved "${template.label}"`);
      onSaved();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to save template");
    } finally {
      setSavePending(false);
    }
  }

  async function reset() {
    setResetPending(true);
    try {
      await resetEmailTemplate(template.template_key);
      showSuccess(`Reset "${template.label}" to default`);
      onSaved();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to reset template");
    } finally {
      setResetPending(false);
    }
  }

  async function sendTest() {
    if (!testTo.trim()) return;
    setTestPending(true);
    setTestResult(null);
    try {
      const result = await testSendEmailTemplate(template.template_key, testTo.trim());
      if (result.sent) {
        setTestResult(`Sent to ${testTo.trim()}`);
        showSuccess("Test email sent");
      } else {
        const detail = result.detail ?? "unknown error";
        setTestResult(`Not sent: ${detail}`);
        showError(detail);
      }
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Failed to send test email";
      setTestResult(message);
      showError(message);
    } finally {
      setTestPending(false);
    }
  }

  return (
    <article className="panel action-card action-form">
      <header className="panel-header">
        <h3>{template.label}</h3>
        <span className={template.is_customized ? "status-pill status-approved" : "status-pill"}>
          {template.is_customized ? "Customized" : "Default"}
        </span>
      </header>

      <label htmlFor={`email-tpl-subject-${template.template_key}`}>Subject</label>
      <input
        id={`email-tpl-subject-${template.template_key}`}
        className="template-subject-input"
        value={edit.subject}
        onChange={(event) => setEdit((prev) => ({ ...prev, subject: event.target.value }))}
      />

      <label htmlFor={`email-tpl-body-${template.template_key}`}>Body</label>
      <textarea
        id={`email-tpl-body-${template.template_key}`}
        className="template-body-input"
        rows={4}
        value={edit.body}
        onChange={(event) => setEdit((prev) => ({ ...prev, body: event.target.value }))}
      />

      <p className="field-hint">
        Available: {template.placeholders.map((placeholder) => `{{${placeholder}}}`).join(" ")}
      </p>

      <div className="action-row">
        <button className="refresh-btn" type="button" disabled={savePending} onClick={() => void save()}>
          {savePending ? "Saving..." : "Save"}
        </button>
        {template.is_customized ? (
          <button
            className="refresh-btn danger-action-btn"
            type="button"
            disabled={resetPending}
            onClick={() => void reset()}
          >
            {resetPending ? "Resetting..." : "Reset to default"}
          </button>
        ) : null}
      </div>

      <div className="action-row">
        <input
          className="search-input"
          value={testTo}
          onChange={(event) => setTestTo(event.target.value)}
          placeholder="Send test email to..."
        />
        <button
          className="refresh-btn"
          type="button"
          disabled={testPending || !testTo.trim()}
          onClick={() => void sendTest()}
        >
          {testPending ? "Sending..." : "Send test email"}
        </button>
      </div>
      {testResult ? <p className="field-hint">{testResult}</p> : null}
    </article>
  );
}

export function EmailTemplatesPanel() {
  const { data, loading, error, refresh } = useAdminResource(() => listEmailTemplates(), []);
  const { data: subscription, loading: subscriptionLoading } = useSubscription();
  const templates = data ?? [];

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Email Templates</h2>
        <span>{templates.length} templates</span>
      </header>
      <p className="management-note">
        Customize the subject and message text of automated emails. Branding, layout, and any
        call-to-action button stay fixed -- only the text below is editable.
      </p>

      {error ? <p className="error-banner">{error}</p> : null}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <LockedFeature
          feature="template_customization"
          featureLabel="Email template customization"
          subscription={subscription}
          loading={subscriptionLoading}
        >
          <div className="panel-grid">
            {templates.map((template) => (
              <EmailTemplateCard
                key={template.template_key}
                template={template}
                onSaved={() => void refresh()}
              />
            ))}
          </div>
        </LockedFeature>
      )}

      <button className="refresh-btn" type="button" onClick={() => void refresh()} disabled={loading}>
        {loading ? "Loading..." : "Refresh"}
      </button>
    </article>
  );
}
